import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import { tenderService, transferWindowService, userService } from "../../services/api";
import { useDebounce } from "../../hooks/useDebounce";
import { usePermission } from "../../hooks/usePermission";
import { useMediaQuery } from "../../hooks/useMediaQuery";
import { effectiveSourceTab } from "../../utils/tenderSourceTabs";
import { useClubWindowCountryDefault } from "../common/useClubWindowCountry";

export const useCoachTenders = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { currentRole } = useSelector((state) => state.auth);

  // Refs to prevent repetitive API calls on mount/Strict Mode
  const fetchedCountriesRef = useRef(false);
  const fetchedCoachProfileRef = useRef(false);
  const fetchedWindowRef = useRef("");
  const lastFetchedTenderParams = useRef("");

  // Permission checks
  const canCreateTenders = usePermission("create_tenders");
  const canViewPitches = usePermission("view_pitches");

  const [coachInClub, setCoachInClub] = useState(false);
  // Initialize tab based on permission - if no view_pitches permission, start with all-tenders
  const [activeMainTab, setActiveMainTab] = useState(() => {
    // The pitch success modal's "View My Pitches" button has always navigated
    // here with { state: { activeTab: "applied" } } (CoachPitchTender.js:100).
    // Honour it, like the agent page does (AgentTenders.js:1161-1163).
    if (location.state?.activeTab) return location.state.activeTab;
    // If coach doesn't have view_pitches permission, default to all-tenders
    // This will be updated after profile loads if coach is in club and has permission
    return canViewPitches ? "my-tenders" : "all-tenders";
  });
  const [profileLoading, setProfileLoading] = useState(true);
  const [activeSubTab, setActiveSubTab] = useState("tp-tenders");
  // Starts EMPTY, not on a hardcoded country. The page used to open on the
  // literal "Spain", which showed a German club the Spanish transfer window as
  // if it were its own. The club's own country is adopted below as soon as it
  // is known; until then (and if it never resolves) nothing is selected and the
  // status line shows its empty placeholder instead of a foreign window.
  const [selectedCountry, setSelectedCountry] = useState("");
  const [tenders, setTenders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showFilterModal, setShowFilterModal] = useState(false);

  // Countries state for API integration
  const [countries, setCountries] = useState([
    { name: "View All", flag: null, isViewAll: true },
  ]);

  // Transfer Window states
  const [isTransferWindowDropdownOpen, setIsTransferWindowDropdownOpen] =
    useState(false);
  const [transferWindow, setTransferWindow] = useState(null);
  const [transferWindowLoading, setTransferWindowLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const dropdownRef = useRef(null);
  const searchInputRef = useRef(null);
  const isMobile = useMediaQuery("(max-width: 768px)");
  const [isYouthCoach, setIsYouthCoach] = useState(false);

  const [filters, setFilters] = useState({
    type: "all",
    market_value_from: "",
    market_value_to: "",
    division: "",
    position: "",
    nationality: "",
    min_age: "",
    max_age: "",
    status: "all",
    search: "",
    only_eu_player: false,
    only_free_players: false,
    sort_by: "",
  });
  const [searchTerm, setSearchTerm] = useState("");
  const debouncedSearchTerm = useDebounce(searchTerm, 500);
  const [sortBy, setSortBy] = useState("pro");
  const [activePositionFilter, setActivePositionFilter] = useState("players");

  // Jobseekers (no club) can only ever apply to STAFF-category requests —
  // the BE refuses everything else at pitch creation (CreatePitchViewSet.
  // perform_create: player tenders demand the Agent group). The BE does NOT
  // limit a coach to coaching roles — every staff-category request is
  // pitchable — so "coaches" here is only the role-natural LANDING category;
  // Staffs and Others remain selectable. The "players" category is not
  // offered to clubless coaches at all. In-club coaches keep the full
  // catalogue including player requests.
  const defaultPositionFilter = coachInClub ? "players" : "coaches";
  const [pagination, setPagination] = useState({
    count: 0,
    next: null,
    previous: null,
    currentPage: 1,
  });

  // Applied tab (self-applications) — own list, own status filter, own
  // pagination, mirroring the agent page's applied tab (AgentTenders.js:1171,
  // 1212-1217). Shares `loading`/`error` with the tender tabs because the
  // tabs are mutually exclusive.
  const [pitches, setPitches] = useState([]);
  const [pitchStatus, setPitchStatus] = useState("all");
  const [pitchPagination, setPitchPagination] = useState({
    count: 0,
    next: null,
    previous: null,
    currentPage: 1,
  });

  const getDefaultFilters = useCallback(
    () => ({
      type: "all",
      market_value_from: "",
      market_value_to: "",
      division: "",
      position: "",
      nationality: "",
      min_age: "",
      max_age: "",
      status: "all",
      search: "",
      only_eu_player: false,
      only_free_players: false,
      sort_by: isYouthCoach ? "youth" : "",
    }),
    [isYouthCoach]
  );

  // Update filters when debounced search term changes
  useEffect(() => {
    setFilters((prev) => ({
      ...prev,
      search: debouncedSearchTerm,
    }));
    setPagination((prev) => ({ ...prev, currentPage: 1 }));
  }, [debouncedSearchTerm]);

  // Fetch coach club status only for Coach role
  useEffect(() => {
    if (currentRole !== "Coach") {
      setProfileLoading(false);
      return;
    }
    if (fetchedCoachProfileRef.current) return;
    fetchedCoachProfileRef.current = true;

    const fetchCoachClubStatus = async () => {
      try {
        const response = await userService.getCoachProfile();
        if (response.success && response.data) {
          // Check if coach works for a club (similar to staff's works_for_club)
          // This might be in response.data.works_for_club or response.data.club_details
          const worksForClub = response.data?.works_for_club || 
                               response.data?.club_details !== null || 
                               false;
          setCoachInClub(worksForClub);
          
          // Check if youth coach (similar to staff's role_type)
          const isYouth = response?.data?.role_type?.includes("youth") || 
                         response?.data?.specialization?.includes("youth") ||
                         false;
          setIsYouthCoach(isYouth);
          
          // Set initial tab based on club status and permissions.
          // Functional update, because the tab may already carry a value
          // handed over via location.state (e.g. "applied" from the pitch
          // success modal) — only the tabs that are invalid for this profile
          // are corrected, an explicitly requested valid tab stays.
          setActiveMainTab((prev) => {
            if (!(worksForClub && canViewPitches)) {
              // No club (or no pitch permission): "My Requests" doesn't exist.
              return prev === "my-tenders" ? "all-tenders" : prev;
            }
            // In-club coaches never self-apply, so the Applied tab is not
            // rendered for them — a stale navigation state must not strand
            // them on an invisible tab.
            return prev === "applied" ? "my-tenders" : prev;
          });
        } else {
          fetchedCoachProfileRef.current = false;
        }
      } catch (error) {
        fetchedCoachProfileRef.current = false;
        console.error("Error fetching coach profile:", error);
        setCoachInClub(false);
      } finally {
        setProfileLoading(false);
      }
    };

    fetchCoachClubStatus();
  }, [currentRole, canViewPitches]);

  // Fetch transfer-window countries from API
  useEffect(() => {
    if (fetchedCountriesRef.current) return;
    fetchedCountriesRef.current = true;

    const fetchCountries = async () => {
      try {
        const result = await transferWindowService.getTransferWindowCountries(
          "mens_professional"
        );
        if (result.success && Array.isArray(result.data)) {
          const countriesData = [
            { name: "View All", flag: null, isViewAll: true },
            ...result.data.map((c) => ({
              name: c.name || c.value,
              flag: c.image_url,
              isViewAll: false,
            })),
          ];
          setCountries(countriesData);
        } else {
          fetchedCountriesRef.current = false;
        }
      } catch (error) {
        fetchedCountriesRef.current = false;
        console.error("Error fetching transfer-window countries:", error);
      }
    };
    fetchCountries();
  }, []);

  // Handle click outside for transfer window dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsTransferWindowDropdownOpen(false);
        setSearchQuery(""); // Reset search when closing
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const fetchTenders = useCallback(
    async (page = 1, signal) => {
      // The Applied tab lists pitches, not tenders — fetchPitches below owns
      // it. Without this guard the shared effect would also fire a tender
      // query with tab=applied and overwrite the list.
      if (activeMainTab === "applied") return;
      const params = new URLSearchParams();
      params.append("page", page);

      if (activeMainTab === "my-tenders") {
        params.append("mine", "true");
      }
      if (activeMainTab === "all-tenders") {
        params.append("view", "all");
      }
      // The TP / TP Lite switch is only offered on All Listed Requests, so on
      // the other tabs the query must read the default source instead of a
      // choice made where the switch was visible — otherwise My Requests asks
      // for is_lite=true and comes back empty. The choice stays in state.
      params.append(
        "is_lite",
        effectiveSourceTab(activeMainTab, activeSubTab) === "tp-lite-tenders"
          ? "true"
          : "false"
      );

      Object.entries(filters).forEach(([key, value]) => {
        if (key === "sort_by" && value) return;
        if (typeof value === "boolean" && value === true) {
          params.append(key, "true");
        } else if (value && value !== "all" && value !== "" && value !== false) {
          params.append(key, value);
        }
      });

      if (activePositionFilter) {
        params.append("tender_category", activePositionFilter);
      }

      let sortValue = filters.sort_by || sortBy;
      if (sortValue === "pro") {
        params.append("recruiting_for", "professional");
      } else if (sortValue === "youth") {
        params.append("recruiting_for", "youth");
      }

      if (sortValue) params.append("sort_by", sortValue);

      // Prevent redundant identical fetches
      const paramsString = params.toString() + `&tab=${activeMainTab}`;
      if (lastFetchedTenderParams.current === paramsString && !signal) return;
      
      const previousParams = lastFetchedTenderParams.current;
      lastFetchedTenderParams.current = paramsString;

      setLoading(true);
      setError(null);

      try {
        const response =
          activeMainTab === "my-tenders"
            ? await tenderService.getClubTenders(params, signal)
            : activeMainTab === "saved"
            ? await tenderService.getSavedTenders(params, signal)
            : await tenderService.getAllTenders(params, signal);

        if (response.success) {
          setTenders(response.data);
          setPagination(response.pagination);
        } else {
          lastFetchedTenderParams.current = previousParams;
          setError(response.error);
        }
      } catch (err) {
        if (err.name === "AbortError" || err.message === "canceled") return;
        lastFetchedTenderParams.current = previousParams;
        setError("Failed to fetch requests. Please try again.");
      } finally {
        if (!signal?.aborted) {
          setLoading(false);
        }
      }
    },
    [activeMainTab, filters, sortBy, activeSubTab, activePositionFilter]
  );

  // The club's own country becomes the initial selection, once, as soon as the
  // profile and the country list have both arrived. A country the user picks
  // themselves is never overwritten.
  useClubWindowCountryDefault(countries, selectedCountry, setSelectedCountry);

  // Fetch transfer window data
  useEffect(() => {
    if (!selectedCountry || selectedCountry === "View All" || fetchedWindowRef.current === selectedCountry) return;
    const previousWindow = fetchedWindowRef.current;
    fetchedWindowRef.current = selectedCountry;

    const fetchTransferWindow = async () => {
      setTransferWindowLoading(true);
      try {
        const response = await transferWindowService.getCurrentWindow(
          selectedCountry,
          "mens_professional"
        );

        if (response.success && response.data) {
          setTransferWindow(response.data);
        } else {
          fetchedWindowRef.current = previousWindow;
        }
      } catch (error) {
        fetchedWindowRef.current = previousWindow;
        console.error("Error fetching transfer window:", error);
      } finally {
        setTransferWindowLoading(false);
      }
    };

    fetchTransferWindow();
  }, [selectedCountry]);

  const formatDateRange = (windowData) => {
    if (!windowData) return "";

    if (windowData.current_start && windowData.current_end) {
      const startDate = new Date(windowData.current_start);
      const endDate = new Date(windowData.current_end);

      const formatDate = (date) => {
        return date.toLocaleDateString("en-GB", {
          day: "numeric",
          month: "short",
          year: "numeric",
        });
      };

      return `${formatDate(startDate)} - ${formatDate(endDate)}`;
    }

    const now = new Date();
    const currentTime = now.getTime();

    const window1Start = new Date(windowData.window_1_start);
    const window1End = new Date(windowData.window_1_end);
    const window2Start = new Date(windowData.window_2_start);
    const window2End = new Date(windowData.window_2_end);

    let startDate, endDate;

    if (
      currentTime >= window1Start.getTime() &&
      currentTime <= window1End.getTime()
    ) {
      startDate = window1Start;
      endDate = window1End;
    } else if (
      currentTime >= window2Start.getTime() &&
      currentTime <= window2End.getTime()
    ) {
      startDate = window2Start;
      endDate = window2End;
    } else if (currentTime < window1Start.getTime()) {
      startDate = window1Start;
      endDate = window1End;
    } else {
      startDate = window2Start;
      endDate = window2End;
    }

    const formatDate = (date) => {
      return date.toLocaleDateString("en-GB", {
        day: "numeric",
        month: "short",
        year: "numeric",
      });
    };

    return `${formatDate(startDate)} - ${formatDate(endDate)}`;
  };

  const handleTransferWindowDropdownToggle = () => {
    setIsTransferWindowDropdownOpen(!isTransferWindowDropdownOpen);
    if (!isTransferWindowDropdownOpen) {
      setTimeout(() => {
        if (searchInputRef.current) {
          searchInputRef.current.focus();
        }
      }, 100);
    }
  };

  const handleCountrySelect = (country) => {
    if (country.isViewAll) {
      navigate("/transfer-windows");
    } else {
      setSelectedCountry(country.name);
    }
    setIsTransferWindowDropdownOpen(false);
    setSearchQuery("");
  };

  // Reset filters when changing tabs
  useEffect(() => {
    const defaultFilters = getDefaultFilters();
    setFilters(defaultFilters);
    setSearchTerm("");
    setSortBy(isYouthCoach ? "youth" : "pro");
    setActivePositionFilter(coachInClub ? "players" : "coaches");
    setPagination((prev) => ({ ...prev, currentPage: 1 }));
  }, [activeMainTab, coachInClub, getDefaultFilters, isYouthCoach]);

  // Fetch tenders when tab changes or filters change
  useEffect(() => {
    if (profileLoading) return;

    const controller = new AbortController();
    fetchTenders(pagination.currentPage, controller.signal);

    return () => {
      controller.abort();
    };
  }, [fetchTenders, pagination.currentPage, profileLoading]);

  // Applied tab — fetch the caller's own pitches. Same endpoint family the
  // agent applied tab uses, staff flavour: /dashboard/staff/pitches/.
  const fetchPitches = useCallback(
    async (page = 1, signal) => {
      const params = new URLSearchParams();
      params.append("page", page);
      if (pitchStatus !== "all") {
        params.append("status", pitchStatus);
      }

      setLoading(true);
      setError(null);

      try {
        const response = await tenderService.getStaffPitches(params, signal);
        if (response.success) {
          setPitches(response.data);
          setPitchPagination({ ...response.pagination, currentPage: page });
        } else {
          setError(response.error);
        }
      } catch (err) {
        if (err.name === "AbortError" || err.message === "canceled") return;
        setError("Failed to fetch your applications. Please try again.");
      } finally {
        if (!signal?.aborted) {
          setLoading(false);
        }
      }
    },
    [pitchStatus]
  );

  useEffect(() => {
    if (profileLoading || activeMainTab !== "applied") return;

    const controller = new AbortController();
    fetchPitches(pitchPagination.currentPage, controller.signal);

    return () => {
      controller.abort();
    };
  }, [activeMainTab, fetchPitches, pitchPagination.currentPage, profileLoading]);

  const handlePitchStatusChange = (status) => {
    setPitchStatus(status || "all");
    setPitchPagination((prev) => ({ ...prev, currentPage: 1 }));
  };

  // Pagination goes through state so the effect above performs the single
  // fetch — calling fetchPitches directly from the buttons would fetch once
  // via the call and once more via the currentPage dependency.
  const handlePitchPageChange = (page) => {
    setPitchPagination((prev) =>
      prev.currentPage === page ? prev : { ...prev, currentPage: page }
    );
  };

  const handleFilterChange = (newFilters) => {
    setPagination((prev) => ({ ...prev, currentPage: 1 }));
    const updatedFilters = {
      ...newFilters,
      sort_by: isYouthCoach ? "youth" : newFilters.sort_by,
    };
    setFilters(updatedFilters);
    const nextSortValue = isYouthCoach
      ? "youth"
      : updatedFilters.sort_by || sortBy || "pro";
    setSortBy(nextSortValue);
    setSearchTerm(updatedFilters.search || "");
    setShowFilterModal(false);
  };

  const clearAllFilters = () => {
    const defaultFilters = getDefaultFilters();
    setFilters(defaultFilters);
    setSearchTerm("");
    setSortBy(isYouthCoach ? "youth" : "pro");
    setActivePositionFilter(defaultPositionFilter);
    setPagination((prev) => ({ ...prev, currentPage: 1 }));
  };

  const hasActiveFilters = () => {
    return (
      filters.type !== "all" ||
      filters.market_value_from ||
      filters.market_value_to ||
      filters.division ||
      filters.position ||
      filters.nationality ||
      filters.min_age ||
      filters.max_age ||
      filters.status !== "all" ||
      filters.search ||
      filters.only_eu_player ||
      filters.only_free_players ||
      (filters.sort_by && (!isYouthCoach || filters.sort_by !== "youth"))
    );
  };

  const getActiveFiltersCount = () => {
    let count = 0;
    if (filters.type !== "all") count++;
    if (filters.market_value_from) count++;
    if (filters.market_value_to) count++;
    if (filters.division) count++;
    if (filters.position) count++;
    if (filters.nationality) count++;
    if (filters.min_age) count++;
    if (filters.max_age) count++;
    if (filters.status !== "all") count++;
    if (filters.search) count++;
    if (filters.only_eu_player) count++;
    if (filters.only_free_players) count++;
    if (filters.sort_by && (!isYouthCoach || filters.sort_by !== "youth")) count++;

    return count;
  };

  const handleSortChange = (value) => {
    if (isYouthCoach) {
      setSortBy("youth");
      setFilters((prev) =>
        prev.sort_by === "youth" ? prev : { ...prev, sort_by: "youth" }
      );
      return;
    }

    setPagination((prev) => ({ ...prev, currentPage: 1 }));
    const nextSort = value || "pro";
    setSortBy(nextSort);
    setFilters((prev) =>
      prev.sort_by === nextSort ? prev : { ...prev, sort_by: nextSort }
    );
  };

  const handlePositionFilterChange = (filterId) => {
    setPagination((prev) => ({ ...prev, currentPage: 1 }));
    const safeFilterId = filterId || defaultPositionFilter;
    setActivePositionFilter(safeFilterId);
  };

  const handleSearchChange = (value) => {
    setSearchTerm(value);
  };

  // Optimistically reflect a save/unsave toggle in the current list, mirroring
  // the agent flow. On the Saved tab an unsaved tender drops out of the list.
  const handleSaveStatusChange = (tenderId, isSaved) => {
    if (activeMainTab === "saved" && !isSaved) {
      setTenders((prev) => prev.filter((tender) => tender.id !== tenderId));
      return;
    }
    setTenders((prev) =>
      prev.map((tender) =>
        tender.id === tenderId ? { ...tender, is_saved: isSaved } : tender
      )
    );
  };

  return {
    navigate,
    currentRole,
    canCreateTenders,
    canViewPitches,
    coachInClub,
    activeMainTab,
    setActiveMainTab,
    activeSubTab,
    setActiveSubTab,
    selectedCountry,
    setSelectedCountry,
    tenders,
    loading,
    error,
    showFilterModal,
    setShowFilterModal,
    countries,
    isTransferWindowDropdownOpen,
    transferWindow,
    transferWindowLoading,
    searchQuery,
    setSearchQuery,
    dropdownRef,
    searchInputRef,
    isMobile,
    isYouthCoach,
    profileLoading,
    filters,
    searchTerm,
    sortBy,
    activePositionFilter,
    pagination,
    formatDateRange,
    handleTransferWindowDropdownToggle,
    handleCountrySelect,
    handleFilterChange,
    clearAllFilters,
    hasActiveFilters,
    getActiveFiltersCount,
    handleSortChange,
    handlePositionFilterChange,
    handleSearchChange,
    fetchTenders,
    handleSaveStatusChange,
    // Applied tab (self-applications)
    pitches,
    pitchStatus,
    handlePitchStatusChange,
    pitchPagination,
    handlePitchPageChange,
  };
};

